import styled from 'particles/themes/styled'
import Timeline, { TimelineProps as BasedTimelineProps } from '@mui/lab/Timeline'
import TimelineDot, { TimelineDotProps as BaseTimelineDotProps } from '@mui/lab/TimelineDot'

interface TimelineProps extends BasedTimelineProps {
    titleOnTopInFirstBreakPoint: boolean
    styleType: 'short' | 'default'
}

export const TimelineContainer = styled.div<TimelineProps>`
    width: ${({ styleType }) => (styleType === 'default' ? '600px' : '344px')};
    border-right: 1px;
    @media screen and (max-width: 768px) {
        margin-top: auto;
        align-items: end;
        width: ${({ titleOnTopInFirstBreakPoint, styleType }) =>
            styleType === 'short'
                ? titleOnTopInFirstBreakPoint
                    ? '202.5px'
                    : '344px'
                : titleOnTopInFirstBreakPoint
                  ? '202.5px'
                  : 'fit-content'};
        display: flex;
        flex-direction: row-reverse;
        align-items: flex-start;
        margin-top: 0;
    }
    @media screen and (max-width: 375px) {
        margin-top: auto;
        align-items: end;
        width: ${({ styleType }) => (styleType === 'short' ? '167.5px' : '351px')};
    }
    .first-row {
        display: flex;
        overflow: hidden;
        gap: ${({ theme }) => theme.spacing[200]};
        @media screen and (max-width: 768px) {
            display: ${({ titleOnTopInFirstBreakPoint }) => (titleOnTopInFirstBreakPoint ? 'block' : 'flex')};
        }
        @media screen and (max-width: 375px) {
            display: block;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: fit-content;
            gap: 0;
            width: 100%;
        }
    }

    .title-container {
        width: ${({ styleType }) => (styleType === 'default' ? '300px' : '175px')};
        @media screen and (max-width: 768px) {
            width: ${({ styleType }) => (styleType === 'default' ? '100%' : '200px')};
            text-align: -webkit-right;
        }
        @media screen and (max-width: 375px) {
            width: ${({ styleType }) => (styleType === 'default' ? 'fit-content' : '167.5px')};
        }
        @media screen and (max-width: 320px) {
            width: ${({ styleType }) => (styleType === 'default' ? 'fit-content' : '167.5px')};
        }
    }
    .title {
        margin-left: auto;
        white-space: wrap;
        @media screen and (max-width: 768px) {
            margin-left: 0;
        }
        @media screen and (max-width: 375px) {
            max-width: 167.5px;
            margin-right: auto;
            margin-left: 0;
        }
    }
    .area {
        width: ${({ styleType }) => (styleType === 'default' ? '300px' : '169px')};
        position: relative;
        @media screen and (max-width: 768px) {
            width: ${({ styleType }) => (styleType === 'default' ? '206.5px' : '200px')};
        }
        @media screen and (max-width: 375px) {
            width: ${({ styleType }) => (styleType === 'default' ? 'fit-content' : '167.5px')};
        }
    }
`
interface StyledTimelineProps {
    styledProgress: number[]
    styleType: 'short' | 'default'
    dotNumber: 2 | 3
}

export const StyledTimeline = styled(Timeline)<StyledTimelineProps>(
    ({ styledProgress, styleType, dotNumber, theme }) => ({
        position: 'absolute',
        height: '24px',
        width: '24px',
        margin: '0px',
        padding: '0px',
        transformOrigin: 'left',
        transform:
            styleType === 'default' ? 'rotate(-90deg) translate(-154%, 25%)' : 'rotate(-90deg) translate(-154%, 45%)',
        paddingInlineStart: '0px',

        '@media screen and (max-width: 768px)': {
            position: 'static',
            transform:
                styleType === 'default'
                    ? 'rotate(-90deg) translate(-154%, 25%)'
                    : 'rotate(-90deg) translate(-154%, 30%)',
        },

        '.MuiTimelineItem-root': {
            minHeight:
                dotNumber === 3
                    ? styleType === 'default'
                        ? '146px'
                        : '78px'
                    : styleType === 'default'
                      ? '292px'
                      : '153px',
            '@media screen and (max-width: 768px)': {
                minHeight:
                    dotNumber === 3
                        ? styleType === 'default'
                            ? '99.25px'
                            : '82px'
                        : styleType === 'default'
                          ? '198.5px'
                          : '194.5px',
            },
            '@media screen and (max-width: 375px)': {
                minHeight:
                    dotNumber === 3
                        ? styleType === 'default'
                            ? '99.25px'
                            : '82px'
                        : styleType === 'default'
                          ? '198.5px'
                          : '159.5px',
            },
        },

        '.MuiTimelineDot-root': {
            margin: '4px 0 4px 0',
            padding: '2px',
            boxShadow: 'none',
        },

        '.MuiTimelineConnector-root': {
            boxSizing: 'border-box',
            width: '4px',
        },

        '.first-line': {
            background: `linear-gradient(${theme.colors.indicator.skyBlue} ${styledProgress[0] + '%'}, ${
                theme.colors.indicator.lightGrey
            } ${styledProgress[0] + '%'})`,
        },

        '.second-line': {
            background: `linear-gradient(${theme.colors.indicator.skyBlue} ${styledProgress[1] + '%'}, ${
                theme.colors.indicator.lightGrey
            } ${styledProgress[1] + '%'})`,
        },

        '&:hover .MuiTimelineDot-root': {
            borderColor: theme.colors.border.action,
            borderWidth: '1px',
            padding: '3px',
        },

        '&:hover .MuiTimelineConnector-root': {
            borderColor: theme.colors.border.action,
            borderWidth: '1px',
            borderStyle: 'solid',
        },
    }),
)

export const PopperContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    .content-divider {
        width: 100%;
        height: 1px;
        background: ${({ theme }) => theme.colors.divider.primary};
    }
    .dot-content-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }
    .dots {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
`

export const DotContentContainer = styled.div`
    width: 296px;
    display: flex;
    gap: ${({ theme }) => theme.spacing[200]};
    .dot {
        height: 24px;
        padding: 8px 0 8px 0;
    }
    .description-container {
        width: 100%;
        white-space: wrap;
    }
    .first-title {
        display: flex;
        flex-direction: row;
        gap: ${({ theme }) => theme.spacing[100]};
    }
`

interface TimelineDotProps extends BaseTimelineDotProps {
    dotColor: string
}

export const StyledTimelineDot = styled(TimelineDot)<TimelineDotProps>(({ dotColor }) => ({
    backgroundColor: dotColor,
    padding: '2px',
    boxShadow: 'none',
    margin: 0,
}))
